import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stack,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../contexts/AuthContext';
import {
  announcementApi,
  AnnouncementSeverity,
  QuickSaleAnnouncementDto,
} from '../../../../../services/announcementApiService';
import announcementSignalRService from '../../../../../services/announcementSignalRService';
import AnnouncementFormModal from './AnnouncementFormModal';

interface Props {
  open: boolean;
  /** general: Şirketin tüm duyuruları, per-screen: Sadece bu ekrana yayınlananlar. */
  mode: 'general' | 'per-screen';
  screenId?: number | null;
  screenName?: string | null;
  /** Per-screen modaldaysa ekranın şubesi (filtre için). */
  branchId?: string | null;
  onClose: () => void;
}

const SEVERITY_META: Record<AnnouncementSeverity, { color: string; bgcolor: string; Icon: typeof InfoOutlinedIcon; label: string }> = {
  Info: { color: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.08)', Icon: InfoOutlinedIcon, label: 'Bilgi' },
  Warning: { color: '#ed6c02', bgcolor: 'rgba(237, 108, 2, 0.10)', Icon: WarningAmberIcon, label: 'Uyarı' },
  Critical: { color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.12)', Icon: ErrorOutlineIcon, label: 'Kritik' },
};

function formatDate(iso: string, isFar?: boolean): string {
  if (isFar) return '∞';
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

function summarizeTargets(a: QuickSaleAnnouncementDto): string {
  if (a.scope === 'Company') return 'Tüm Şirket';
  const targets = a.targets ?? [];
  const branchCount = targets.filter((t) => t.targetType === 'Branch').length;
  const screenCount = targets.filter((t) => t.targetType === 'Screen').length;
  if (a.scope === 'Branches') return `${branchCount} şube`;
  if (a.scope === 'Screen') return `${screenCount} ekran`;
  return '-';
}

function summarizeRecurrence(a: QuickSaleAnnouncementDto): string {
  const parts: string[] = [];
  switch (a.recurrencePattern) {
    case 'Once': parts.push('Tek seferlik'); break;
    case 'Daily': parts.push('Her gün'); break;
    case 'Weekly': parts.push('Haftalık'); break;
    case 'Monthly': parts.push(`Her ayın ${a.monthDay ?? '?'}'i`); break;
    default: break;
  }
  if (a.weekdaysCsv) parts.push(`(${a.weekdaysCsv.split(',').map((d) => 'PztSalÇarPerCumCmtPaz'.substring((parseInt(d, 10) - 1) * 3, parseInt(d, 10) * 3)).join(', ')})`);
  if (a.dailyStartTime && a.dailyEndTime) parts.push(`${a.dailyStartTime.substring(0, 5)}-${a.dailyEndTime.substring(0, 5)}`);
  return parts.join(' ');
}

/** Severity güvenli normalize: bozuk/eksik veriye karşı Info'ya düşer. */
function normalizeSeverity(s: any): AnnouncementSeverity {
  if (s === 'Critical' || s === 'Warning' || s === 'Info') return s;
  if (typeof s === 'string') {
    const upper = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    if (upper === 'Critical' || upper === 'Warning' || upper === 'Info') return upper as AnnouncementSeverity;
  }
  return 'Info';
}

const AnnouncementsManagementModal: React.FC<Props> = ({
  open,
  mode,
  screenId,
  screenName,
  branchId,
  onClose,
}) => {
  const { t } = useTranslation('salesSystem');
  const { userDetails } = useAuth();
  const role = userDetails?.role;
  const canManage = role === 'admin' || role === 'owner';

  const [items, setItems] = useState<QuickSaleAnnouncementDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<QuickSaleAnnouncementDto | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  /** Silme onay dialog'u için seçilen duyuru (null ise dialog kapalı). */
  const [deleteTarget, setDeleteTarget] = useState<QuickSaleAnnouncementDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      let list: QuickSaleAnnouncementDto[] = [];
      if (mode === 'per-screen' && screenId) {
        list = await announcementApi.getAllForScreen(screenId, branchId ?? null);
      } else {
        list = await announcementApi.getAll();
      }
      setItems(list);
    } catch (err: any) {
      console.error('[AnnouncementsManagementModal] fetch hatası:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('announcements.modal.errorLoad', 'Duyurular yüklenemedi.')
      );
    } finally {
      setLoading(false);
    }
  }, [open, mode, screenId, branchId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // SignalR ile başka kullanıcı/sekme oluştur/sil/güncelle yaparsa anında refresh
  useEffect(() => {
    if (!open) return;
    const cbId = `mgmt_modal_${mode}_${screenId ?? 'all'}`;
    announcementSignalRService.onAnnouncementChanged(cbId, () => {
      fetchData();
    });
    return () => announcementSignalRService.removeAnnouncementChangedCallback(cbId);
  }, [open, mode, screenId, fetchData]);

  // Tek liste: aktifler önce gelsin, sonra inaktifler. Her ikisi de createdAt yeni → eski.
  const visible = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.isCurrentlyActive !== b.isCurrentlyActive) {
        return a.isCurrentlyActive ? -1 : 1;
      }
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [items]);

  const activeCount = useMemo(() => items.filter((i) => i.isCurrentlyActive).length, [items]);
  const inactiveCount = items.length - activeCount;

  const handleAdd = () => {
    setEditTarget(null);
    setFormOpen(true);
  };

  const handleEdit = (a: QuickSaleAnnouncementDto) => {
    setEditTarget(a);
    setFormOpen(true);
  };

  /** Silme isteği — sadece onay dialog'unu açar. */
  const handleDelete = (a: QuickSaleAnnouncementDto) => {
    setDeleteError(null);
    setDeleteTarget(a);
  };

  /** Silme onaylandığında çalışır — gerçek API çağrısı burada. */
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const a = deleteTarget;
    setDeletingId(a.id);
    setDeleteError(null);
    try {
      await announcementApi.softDelete(a.id);
      await fetchData();
      setDeleteTarget(null);
    } catch (err: any) {
      console.error('[AnnouncementsManagementModal] delete hatası:', err);
      setDeleteError(
        err?.response?.data?.message ||
          err?.message ||
          t('announcements.modal.errorDelete', 'Duyuru silinemedi.')
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    if (deletingId !== null) return; // silme devam ederken kapatma
    setDeleteTarget(null);
    setDeleteError(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CampaignIcon color="primary" />
            <Box>
              <Typography variant="h6" component="div">
                {mode === 'per-screen'
                  ? t('announcements.modal.titlePerScreen', 'Bu Ekrana Özel Duyurular')
                  : t('announcements.modal.titleGeneral', 'Genel Duyuru Sistemi')}
              </Typography>
              {mode === 'per-screen' && screenName && (
                <Typography variant="caption" color="text.secondary">
                  {screenName}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={fetchData} size="small" title={t('announcements.modal.refresh', 'Yenile')}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!loading && items.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('announcements.modal.summary', 'Toplam')}: {items.length}
              </Typography>
              <Chip
                label={`${t('announcements.modal.active', 'Yayında')}: ${activeCount}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 22 }}
              />
              <Chip
                label={`${t('announcements.modal.inactive', 'Yayında değil')}: ${inactiveCount}`}
                size="small"
                color="default"
                variant="outlined"
                sx={{ height: 22 }}
              />
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : visible.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <CampaignIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
              <Typography>
                {t('announcements.modal.empty', 'Henüz duyuru yok.')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {visible.map((a) => {
                const sev = normalizeSeverity(a.severity);
                const meta = SEVERITY_META[sev];
                const Icon = meta.Icon;
                const isFarFuture = a.endAt ? new Date(a.endAt).getFullYear() >= 2099 : false;
                return (
                  <Paper
                    key={a.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderLeft: `4px solid ${meta.color}`,
                      bgcolor: meta.bgcolor,
                      opacity: a.isCurrentlyActive ? 1 : 0.65,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Icon sx={{ color: meta.color, mt: 0.5 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                            {a.title || '(Başlıksız)'}
                          </Typography>
                          <Chip
                            label={t(`announcements.severity.${sev.toLowerCase()}`, meta.label)}
                            size="small"
                            sx={{ bgcolor: meta.color, color: 'white', height: 20, fontSize: '0.7rem' }}
                          />
                          <Chip
                            label={a.isCurrentlyActive
                              ? t('announcements.modal.statusActive', 'Yayında')
                              : t('announcements.modal.statusInactive', 'Yayında değil')}
                            size="small"
                            color={a.isCurrentlyActive ? 'success' : 'default'}
                            variant={a.isCurrentlyActive ? 'filled' : 'outlined'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Chip
                            label={summarizeTargets(a)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                          {a.message || ''}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {a.startAt ? formatDate(a.startAt) : '—'} → {a.endAt ? formatDate(a.endAt, isFarFuture) : '—'}
                          </Typography>
                          <Divider orientation="vertical" flexItem />
                          <Typography variant="caption" color="text.secondary">
                            {summarizeRecurrence(a)}
                          </Typography>
                          {a.createdByName && (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Typography variant="caption" color="text.secondary">
                                {t('announcements.modal.createdBy', 'Oluşturan')}: {a.createdByName}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </Box>
                      {canManage && (
                        a.systemSource ? (
                          <Tooltip
                            title={t(
                              'announcements.modal.systemLocked',
                              'Bu duyuru sistem tarafından yönetiliyor (ör. yenileme/tahsilat) ve buradan düzenlenemez veya silinemez. Ödeme yapıldığında otomatik kapanır.'
                            )}
                          >
                            <Chip
                              icon={<LockIcon sx={{ fontSize: 16 }} />}
                              label={t('announcements.modal.systemManaged', 'Sistem')}
                              size="small"
                              variant="outlined"
                              color="default"
                              sx={{ height: 24 }}
                            />
                          </Tooltip>
                        ) : (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t('announcements.modal.edit', 'Düzenle')}>
                              <IconButton size="small" onClick={() => handleEdit(a)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('announcements.modal.delete', 'Sil')}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(a)}
                                  disabled={deletingId === a.id}
                                >
                                  {deletingId === a.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        )
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            {!canManage && (
              <Typography variant="caption" color="text.secondary">
                {t('announcements.modal.readonlyHint', 'Görüntüleme modu — düzenleme için admin yetkisi gerekir.')}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            {canManage && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
                {t('announcements.modal.addNew', 'Yeni Duyuru Ekle')}
              </Button>
            )}
            <Button onClick={onClose}>
              {t('announcements.modal.close', 'Kapat')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <AnnouncementFormModal
        open={formOpen}
        mode={editTarget ? 'edit' : 'create'}
        initialData={editTarget}
        fixedScreenId={mode === 'per-screen' ? screenId ?? null : null}
        fixedScreenName={mode === 'per-screen' ? screenName ?? null : null}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          fetchData();
        }}
      />

      {/* ✨ Silme Onay Modalı — window.confirm yerine MUI Dialog (kullanıcı isteği) */}
      <Dialog
        open={deleteTarget !== null}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
          <WarningAmberIcon sx={{ color: 'error.main' }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
            {t('announcements.modal.deleteTitle', 'Duyuru Silinsin Mi?')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {t(
              'announcements.modal.deleteConfirmIntro',
              'Aşağıdaki duyuru kalıcı olarak silinecek. Bu işlem geri alınamaz.'
            )}
          </Typography>
          {deleteTarget && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderLeft: `4px solid ${SEVERITY_META[normalizeSeverity(deleteTarget.severity)].color}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {deleteTarget.title || '(Başlıksız)'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteTarget.message || ''}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCancelDelete}
            disabled={deletingId !== null}
            variant="outlined"
          >
            {t('announcements.modal.deleteCancel', 'Vazgeç')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deletingId !== null}
            variant="contained"
            color="error"
            startIcon={
              deletingId !== null ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deletingId !== null
              ? t('announcements.modal.deleting', 'Siliniyor…')
              : t('announcements.modal.deleteConfirmBtn', 'Evet, Sil')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AnnouncementsManagementModal;
